import React from 'react'
import { FieldErrors, FieldValues } from 'react-hook-form'

import styled from '@emotion/styled'
import { DEFAULT_THEME_COLORS } from 'css/theme'

import Label from 'modules/ui/atoms/Typography/Label'
import InputField from 'modules/ui/molecules/FormComponents/InputField'
import Placeloader from 'modules/ui/atoms/Loaders/Placeloader'
import SelectField from 'modules/ui/molecules/FormComponents/SelectField'
import { UiIcon } from 'modules/ui/atoms/Icon'

import Column from 'modules/ui/atoms/Layout/Column'
import Row from 'modules/ui/atoms/Layout/Row'

import { useTranslation } from 'next-i18next'
import { useAddressForm } from './useAddressForm'

// Generic interface that works with any form data containing address fields
interface OrderAddressProps<T extends FieldValues = FieldValues> {
  errors: FieldErrors<T>
  watchCountry: string
  watchState: string
}

// id names are very important for both autofill & autocomplete to work as expected
const FIELD_IDS = {
  country: 'country',
  addressLine1: 'addressLine1',
  addressLine2: 'addressLine2',
  city: 'city',
  state: 'state',
  zipCode: 'zipCode',
}

export default function OrderAddress<T extends FieldValues = FieldValues>({
  errors,
  watchCountry,
  watchState,
}: OrderAddressProps<T>) {
  const { t } = useTranslation()

  const {
    showLine2Input,
    autoComplete,
    handleAddLine2Click,
    handleAddLine2KeyPress,
    handleKeyDown,
    handleOnFocus,
    handleOnBlur,
    googlePlacesRef: ref,
    countryOptions: countries,
    stateOptions,
    statesIsFetching,
    hasStates,
    getStateLabel,
  } = useAddressForm({
    fieldIds: FIELD_IDS,
    enableLine2: true,
    watchCountry,
  })

  const StateField = () => {
    let field = null

    if (statesIsFetching) {
      const StyledStateField = styled.div`
        margin-top: 12px;
      `

      field = (
        <StyledStateField>
          <Label size="sm">{t('OrderAddress.label.stateRequired', 'State')}</Label>
          <Placeloader
            style={{ minHeight: '46px', borderRadius: '8px', margin: '4px 0', width: '100%' }}
          />
        </StyledStateField>
      )
    } else {
      const label = getStateLabel()

      field = (
        <SelectField
          required={hasStates}
          id={FIELD_IDS.state}
          label={label}
          error={errors.state as { message: string }}
          options={stateOptions}
          autoComplete="address-level1"
          dataId="address-state-select"
          value={watchState}
          disabled={!hasStates}
        />
      )
    }

    return field
  }

  return (
    <Column>
      <Row>
        <Column>
          <InputField
            required
            id={FIELD_IDS.addressLine1}
            label={t('OrderAddress.label.address', 'Address')}
            error={errors.addressLine1 as { message: string }}
            onKeyDown={handleKeyDown}
            inputRef={ref}
            overwriteValueWithFocus={true}
            autoComplete={autoComplete}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            placeholder={t('AddressFields.address.placeholder', 'Enter a location')}
            dataId="address-line1-input"
          />

          {!showLine2Input && (
            <AddLine2Container
              role="button"
              tabIndex={0}
              data-id="address-line2-toggle"
              onClick={handleAddLine2Click}
              onKeyDown={handleAddLine2KeyPress}
            >
              <UiIcon
                w={22}
                h={22}
                name="add_circle"
                color={DEFAULT_THEME_COLORS['--theme-color--brand-primary']}
              />

              <Label size="sm" color={DEFAULT_THEME_COLORS['--theme-color--brand-primary']}>
                {t('OrderAddress.label.addLine2Prompt', 'Add apt., suite, floor')}
              </Label>
            </AddLine2Container>
          )}

          {showLine2Input && (
            <Row>
              <Column style={{ flex: 1 }}>
                <InputField
                  id={FIELD_IDS.addressLine2}
                  label={t('OrderAddress.label.address2Optional', 'Address 2 (optional)')}
                  placeholder={t('OrderAddress.placeholder.address2', 'Apt., suite, floor')}
                  error={errors.addressLine2 as { message: string }}
                  autoComplete="address-line2"
                  dataId="address-line2-input"
                />
              </Column>
            </Row>
          )}
        </Column>
      </Row>

      <Row>
        <Column>
          <InputField
            required
            id={FIELD_IDS.zipCode}
            dataId="address-zipcode-input"
            error={errors.zipCode as { message: string }}
            label={
              watchCountry == 'US'
                ? t('OrderAddress.label.zipCode', 'Zip Code')
                : t('OrderAddress.label.postalCode', 'Postal Code')
            }
            autoComplete="postal-code"
          />
        </Column>
        <Column>
          <InputField
            required
            id={FIELD_IDS.city}
            label={t('OrderAddress.label.city', 'City')}
            dataId="address-city-input"
            error={errors.city as { message: string }}
            autoComplete="address-level2"
          />
        </Column>
      </Row>

      <Row>
        <Column>{StateField()}</Column>
        <Column>
          <SelectField
            required
            id={FIELD_IDS.country}
            label={t('OrderAddress.label.country', 'Country')}
            dataId="address-country-input"
            error={errors.country as { message: string }}
            options={countries}
            autoComplete="country"
            value={watchCountry}
          />
        </Column>
      </Row>
    </Column>
  )
}

const AddLine2Container = styled(Row)`
  cursor: pointer;
  width: fit-content;
  align-items: center;
  gap: 2px;
`
